import {Text, Toggle} from '@/components-v2/atoms'
import {useBuySingleProductCondition} from '@/containers/Promotions/components/PromotionsConditions/useBuySingleProductCondition'
import PromotionSelectionModalV2 from '@/features/Promotions/components/PromotionSelectionModalV2'
import {RewardType} from '@/types/promotion'
import {AmountInput, ProductsDropdown, QuantityDropdown} from './ConditionFields'
import {RewardItem} from './Rewards'

/**
 * v2 "Buy <quantity|amount> <products> and get <reward>" row for BUY_PRODUCT promotions.
 * State and handlers live in useBuySingleProductCondition, shared with the legacy view.
 */
export const BuySingleProductCondition = ({promotionCondition, index, selectedPromotionCondition = 'UN'}) => {
  const {
    isLoadingConfig,
    buyTypeOptions,
    buyQtyOptions,
    currentCondition,
    hasMinQuantity,
    isMinQuantityFeatureEnabled,
    mainView,
    minView,
    modalProps,
    isFirstCondition,
    isError,
    isMinQuantityError,
    isQuantityTab,
    currencyCode,
    textAreavalue,
    handleBuyCount,
    handleMinQuantity,
    handleToggleMinQuantity,
    handleBuyAmount,
    handleMinAmount,
    handleSelectedBuyItem,
    handleSelectedMinBuyItem,
    handleSubmitModal,
    handleCloseModal,
  } = useBuySingleProductCondition({promotionCondition, index, selectedPromotionCondition})

  const isMinModal = modalProps.target === 'min'
  const isFreeProductFromList = promotionCondition?.rewards?.[0]?.type === RewardType.FREE_PRODUCT_FROM_LIST

  return (
    <div className="flex flex-col gap-4">
      {index === 0 && isMinQuantityFeatureEnabled && (
        <Toggle
          checked={hasMinQuantity}
          onUpdate={handleToggleMinQuantity}
          size="small"
          label="Enable Minimum Quantity"
          disabled={isFreeProductFromList}
        />
      )}
      <div className="flex flex-wrap items-baseline gap-4">
        <Text>Buy</Text>

        {isQuantityTab ? (
          <QuantityDropdown
            options={buyQtyOptions}
            value={mainView.quantityValue}
            onChange={handleBuyCount}
            isLoading={isLoadingConfig}
            isRequired={isFirstCondition}
            hasError={isError}
          />
        ) : (
          <AmountInput
            currencyCode={currencyCode}
            value={textAreavalue?.toString() || ''}
            onChange={handleBuyAmount}
            hasError={isError}
          />
        )}

        <ProductsDropdown
          options={buyTypeOptions}
          value={mainView.productsValue}
          selectedText={mainView.selectedText}
          onChange={handleSelectedBuyItem}
          isLoading={isLoadingConfig}
          isRequired={isFirstCondition}
          disabled={index > 0}
        />

        {hasMinQuantity && (
          <>
            <Text>, with min quantity of</Text>
            {isQuantityTab ? (
              <QuantityDropdown
                options={buyQtyOptions}
                value={minView.quantityValue}
                onChange={handleMinQuantity}
                isLoading={isLoadingConfig}
                hasError={isMinQuantityError}
              />
            ) : (
              <AmountInput
                currencyCode={currencyCode}
                value={minView.textValue}
                onChange={handleMinAmount}
                hasError={isMinQuantityError}
              />
            )}
            {/* Min quantity products are picked from the already-selected main products */}
            <ProductsDropdown
              options={mainView.productsValue}
              value={minView.productsValue}
              selectedText={minView.selectedText}
              onChange={handleSelectedMinBuyItem}
              isLoading={isLoadingConfig}
              disabled={index > 0 || !mainView.productsValue.length}
            />
          </>
        )}

        <Text>and get</Text>
        <RewardItem
          index={index}
          promotionCondition={promotionCondition}
          selectedPromotionCondition={selectedPromotionCondition}
        />
      </div>

      <PromotionSelectionModalV2
        key={JSON.stringify(modalProps.type)}
        show={modalProps.show}
        type={modalProps.type}
        onClose={handleCloseModal}
        onSubmit={handleSubmitModal}
        conditionValues={{
          itemType: (isMinModal ? currentCondition?.minItemType : currentCondition?.itemType) || '',
          values: isMinModal ? minView.productsValue : mainView.productsValue,
          productCombination: currentCondition?.productCombination,
        }}
      />
    </div>
  )
}
